import { useState, forwardRef, useImperativeHandle } from 'react'
import type { Session } from '@supabase/supabase-js'
import { Plus, History, ArrowLeft, Maximize2, Minimize2, Search } from 'lucide-react'

// Hooks
import { useRagConversations } from './hooks/useRagConversations'
import { useAgentChat } from './hooks/useAgentChat'

// Components
import { ChatMessages } from './components/ChatMessages'
import { ChatInput } from './components/ChatInput'
import { ConversationHistory } from './components/ConversationHistory'

// Types
import type { Agent } from '../../../services/agentService'
import { useUser } from '../../../contexts/UserContext'

// Stub for RAG System as an Agent (Visuals only)
const RAG_AGENT_STUB: Agent = {
    id: 'RAG_SYSTEM',
    name: 'Orion RAG',
    description: 'Sistema de Recuperação Aumentada para análise de documentos.',
    handle: 'rag-system',
    created_at: new Date().toISOString(),
    user_id: 'system',
    avatar_url: null,
    banner_url: null,
    function_description: 'Análise de documentos com RAG',
    status: 'active',
    version: '1.0.0'
}

export interface ChatRAGMasterRef {
    startNewConversation: () => void
    setShowHistory: (show: boolean) => void
}

interface ChatRAGMasterProps {
    session: Session | null
    isFullscreen?: boolean
    onFullscreenChange?: (isFullscreen: boolean) => void
    agentId?: string
}

export const ChatRAGMaster = forwardRef<ChatRAGMasterRef, ChatRAGMasterProps>(({
    session,
    isFullscreen = false,
    onFullscreenChange,
    agentId
}, ref) => {
    const { user } = useUser()
    const userId = user?.id || session?.user?.id || null
    // const token = session?.access_token || null // Not needed for useAgentChat (handled by LLMContext)

    // Debug identifying agent
    if (agentId) {
        // console.log('[ChatRAGMaster] Initialized for:', agentId)
    }

    // 1. Conversations Management
    const {
        conversations,
        currentConversationId,
        createConversation,
        switchConversation,
        deleteConversation,
        loadConversations
    } = useRagConversations(userId, 'RAG_SYSTEM')

    // 2. Chat Logic (using new Agent Hook)
    const {
        messages,
        isLoading: isLoadingMessages,
        sendMessage,
        error: _chatError,
        streamingMessage
    } = useAgentChat(
        currentConversationId,
        userId,
        agentId || 'RAG_SYSTEM'
    )

    // 3. UI State
    const [inputMessage, setInputMessage] = useState('')
    const [showHistory, setShowHistory] = useState(false)
    const [copiedMessageId, setCopiedMessageId] = useState<number | null>(null)
    const [attachedFiles] = useState<any[]>([]) // Files not yet implemented in V1 RAG hook

    // Refs
    useImperativeHandle(ref, () => ({
        startNewConversation: () => {
            createConversation()
            setShowHistory(false)
        },
        setShowHistory: (show) => setShowHistory(show)
    }))

    // Handlers
    const handleSendMessage = async () => {
        if (!inputMessage.trim()) return

        let targetConversationId = currentConversationId

        // Create conversation if none exists
        if (!targetConversationId) {
            const newConv = await createConversation()
            if (newConv) {
                targetConversationId = newConv.id
            } else {
                return // Error creating conversation
            }
        }

        await sendMessage(inputMessage.trim())
        setInputMessage('')

        // Refresh conversations list if it was empty/new
        if (messages.length === 0) {
            loadConversations()
        }
    }

    const handleCopy = async (content: string, index: number) => {
        try {
            await navigator.clipboard.writeText(content)
            setCopiedMessageId(index)
            setTimeout(() => setCopiedMessageId(null), 2000)
        } catch (error) {
            console.error('Failed to copy', error)
        }
    }

    // Render
    return (
        <div className="h-full flex flex-col px-6 py-6 overflow-hidden">
            {/* Header */}
            <div className="w-full shrink-0 h-10 flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2">
                        <Search className="w-5 h-5 text-indigo-400" />
                        <h3 className="text-lg font-semibold text-white">
                            RAG Query
                        </h3>
                    </div>
                    {currentConversationId && (
                        <span className="text-xs px-2 py-0.5 rounded bg-white/10 text-gray-400 ml-2">
                            {conversations.find(c => c.id === currentConversationId)?.title || 'Nova Conversa'}
                        </span>
                    )}
                </div>

                <div className="flex items-center gap-1">
                    {/* History Toggle */}
                    {showHistory ? (
                        <button
                            onClick={() => setShowHistory(false)}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors flex items-center gap-2"
                        >
                            <ArrowLeft className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                            <span className="text-sm font-medium hidden md:inline">Voltar</span>
                        </button>
                    ) : (
                        <>
                            <button
                                onClick={() => createConversation()}
                                className="p-2 hover:bg-white/10 rounded transition-colors text-gray-400 hover:text-white"
                                title="Nova Conversa"
                            >
                                <Plus className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => setShowHistory(true)}
                                className="p-2 hover:bg-white/10 rounded transition-colors text-gray-400 hover:text-white"
                                title="Histórico"
                            >
                                <History className="w-4 h-4" />
                            </button>
                        </>
                    )}

                    {/* Fullscreen Toggle */}
                    {onFullscreenChange && (
                        <button
                            onClick={() => onFullscreenChange(!isFullscreen)}
                            className="p-2 hover:bg-white/10 rounded transition-colors ml-1 text-gray-400 hover:text-white"
                            title={isFullscreen ? "Minimizar" : "Maximizar"}
                        >
                            {isFullscreen ? (
                                <Minimize2 className="w-4 h-4" />
                            ) : (
                                <Maximize2 className="w-4 h-4" />
                            )}
                        </button>
                    )}
                </div>
            </div>

            {/* Main Content Area - with background for Chat */}
            <div className="flex-1 overflow-hidden relative flex flex-col bg-white/5 border border-white/10 rounded-lg">
                {showHistory ? (
                    <ConversationHistory
                        conversations={conversations.map(conv => ({
                            id: conv.id,
                            title: conv.title || 'Nova Conversa',
                            timestamp: new Date(conv.created_at).getTime()
                        }))}
                        currentConversationId={currentConversationId}
                        onConversationClick={(conv) => {
                            switchConversation(conv.id)
                            setShowHistory(false)
                        }}
                        onConversationDelete={(id, e) => {
                            e.stopPropagation()
                            deleteConversation(id)
                        }}
                        onClose={() => setShowHistory(false)}
                    />
                ) : (
                    <>
                        {/* Messages */}
                        <div className="flex-1 overflow-hidden flex flex-col">
                            <ChatMessages
                                messages={messages}
                                isLoading={isLoadingMessages}
                                agentName={RAG_AGENT_STUB.name}
                                agent={RAG_AGENT_STUB}
                                onCopy={handleCopy}
                                copiedMessageId={copiedMessageId}
                                streamingMessage={streamingMessage}
                            />
                        </div>

                        {/* Input */}
                        <div className="shrink-0 p-4 border-t border-white/10 bg-black/20">
                            <ChatInput
                                value={inputMessage}
                                onChange={setInputMessage}
                                onSend={handleSendMessage}
                                attachedFiles={attachedFiles}
                                placeholder="Pergunte sobre seus documentos..."
                            // No file attachment/voice support in V1
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    )
})
